// src/components/Documents/InteractivePDFViewer.js (FIXED POSITIONING)
import React, { useState, useRef, useEffect } from 'react';
import { 
  Download, 
  ExternalLink, 
  ZoomIn, 
  ZoomOut, 
  RefreshCw,
  AlertCircle,
  Eye,
  FileText,
  Settings,
  Link as LinkIcon,
  Navigation
} from 'lucide-react';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import InteractiveLinkModal from '../PDF/InteractiveLinkModal';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

const InteractivePDFViewer = ({ document, interceptLinks = true }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [linkModal, setLinkModal] = useState({ isOpen: false, url: '', text: '' });
  const [linkOverlays, setLinkOverlays] = useState([]);
  const [linkCount, setLinkCount] = useState(0);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    loadPDF();
    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [document]);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale, interceptLinks]);

  const loadPDF = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const loadingTask = getDocument({
        url: document.url,
        useWorkerFetch: false,
        isEvalSupported: false
      });
      
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      
      // Count total links across all pages
      let totalLinks = 0;
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const annotations = await page.getAnnotations();
        const linkAnnotations = annotations.filter(ann => 
          ann.subtype === 'Link' && (ann.url || ann.dest)
        );
        totalLinks += linkAnnotations.length;
      }
      setLinkCount(totalLinks);
      
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Failed to load PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const renderPage = async (pageNumber) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render the page
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
      // Create link overlays
      await createLinkOverlays(page, viewport);
      
    } catch (err) {
      console.error('Error rendering page:', err);
      setError('Failed to render page');
    }
  };

  const createLinkOverlays = async (page, viewport) => {
    try {
      const annotations = await page.getAnnotations();
      
      const linkAnnotations = annotations.filter(ann => {
        return ann.subtype === 'Link' && (ann.url || ann.dest);
      });
      
      console.log('Link annotations found:', linkAnnotations);
      
      const overlays = linkAnnotations.map((annotation, index) => {
        const rect = annotation.rect;
        if (!rect || rect.length !== 4) {
          return null;
        }
        
        // FIXED: Proper coordinate transformation
        // PDF.js rect format: [x1, y1, x2, y2] where y1 < y2
        const [x1, y1, x2, y2] = rect;
        
        // Transform using the viewport transformation
        const topLeft = viewport.convertToViewportPoint(x1, y2);     // Top-left corner
        const bottomRight = viewport.convertToViewportPoint(x2, y1); // Bottom-right corner
        
        const left = topLeft[0];
        const top = topLeft[1];
        const width = bottomRight[0] - topLeft[0];
        const height = bottomRight[1] - topLeft[1];
        
        console.log(`Link ${index} corrected position:`, { 
          original: rect, 
          transformed: { left, top, width, height }, 
          url: annotation.url 
        });
        
        return {
          id: `link-${index}`,
          left: left,
          top: top,
          width: width,
          height: height,
          url: annotation.url || `#dest-${JSON.stringify(annotation.dest)}`,
          text: annotation.content || annotation.url || 'Link'
        };
      }).filter(Boolean);
      
      setLinkOverlays(overlays);
      
    } catch (err) {
      console.error('Error creating link overlays:', err);
      setLinkOverlays([]);
    }
  };

  const handleLinkClick = (url, text) => {
    console.log('Link clicked:', { url, text, interceptLinks });
    if (interceptLinks) {
      setLinkModal({
        isOpen: true,
        url: url,
        text: text
      });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const downloadDocument = () => {
    const link = document.createElement('a');
    link.href = document.url;
    link.download = document.name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(document.url, '_blank', 'noopener,noreferrer');
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= numPages) {
      setCurrentPage(pageNumber);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Loading interactive PDF...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadPDF}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      {/* Controls */}
      <div className="bg-gray-50 border-b px-4 py-3 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Navigation className="w-4 h-4 rotate-180" />
            </button>
            
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max={numPages}
                value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value))}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
              />
              <span className="text-sm text-gray-600">of {numPages}</span>
            </div>
            
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= numPages}
              className="p-1 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Navigation className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className={`flex items-center space-x-2 text-sm px-3 py-1 rounded-full ${
              interceptLinks ? 'text-blue-600 bg-blue-100 border border-blue-200' : 'text-gray-600 bg-gray-100 border border-gray-200'
            }`}>
              <LinkIcon className="w-4 h-4" />
              <span className="font-medium">{linkCount} total • {linkOverlays.length} current page</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white">
                {interceptLinks ? 'Interactive' : 'Direct'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={zoomOut}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 min-w-16 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          
          <button
            onClick={loadPDF}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          
          <button
            onClick={openInNewTab}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Open in New Tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          
          <button
            onClick={downloadDocument}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* PDF Display */}
      <div className="relative bg-gray-100 flex justify-center p-4 overflow-auto" style={{ minHeight: '600px' }}>
        <div ref={containerRef} className="relative shadow-lg bg-white">
          <canvas
            ref={canvasRef}
            className="block"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
          
          {/* Custom Link Overlays with improved positioning */}
          {linkOverlays.map((overlay) => (
            <div
              key={overlay.id}
              onClick={() => handleLinkClick(overlay.url, overlay.text)}
              className="absolute cursor-pointer transition-all duration-200"
              style={{
                left: `${overlay.left}px`,
                top: `${overlay.top}px`,
                width: `${overlay.width}px`,
                height: `${overlay.height}px`,
                backgroundColor: interceptLinks ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                border: interceptLinks ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent',
                borderRadius: '3px',
                zIndex: 1000
              }}
              title={`Click to interact: ${overlay.url}`}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.25)';
                e.target.style.border = '2px solid rgba(59, 130, 246, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = interceptLinks ? 'rgba(59, 130, 246, 0.15)' : 'transparent';
                e.target.style.border = interceptLinks ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid transparent';
              }}
            />
          ))}
        </div>
      </div>

      {/* Interactive Link Modal */}
      <InteractiveLinkModal
        isOpen={linkModal.isOpen}
        onClose={() => setLinkModal({ isOpen: false, url: '', text: '' })}
        linkUrl={linkModal.url}
        linkText={linkModal.text}
      />
    </div>
  );
};

export default InteractivePDFViewer;